<template>
  <div>
    <my-notification :icon="'users'">
      <template #title>{{ $t('compSuperadminUsersAdmin.title') }} </template>
      {{ $t('compSuperadminUsersAdmin.info') }}
    </my-notification>
    <br />
    <div class="columns">
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">
                {{ $t('compSuperadminUsersAdmin.ninactive') }}
              </p>
              <div class="title is-size-2">
                <p>
                  {{ ninactives }}
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">
                {{ $t('compSuperadminUsersAdmin.nusers') }}
              </p>
              <div class="title is-size-2">
                <p>
                  {{ nusers }}
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">
                {{ $t('compSuperadminUsersAdmin.nsuperuser') }}
              </p>
              <div class="title is-size-2">
                <p>
                  {{ nsuperusers }}
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
      <div class="column">
        <b-notification :closable="false">
          <div class="level-item has-text-centered">
            <div>
              <p class="heading">
                {{ $t('compSuperadminUsersAdmin.nreviewer') }}
              </p>
              <div class="title is-size-2">
                <p>
                  {{ nreviewers }}
                </p>
              </div>
            </div>
          </div>
        </b-notification>
      </div>
    </div>
    <b-table
      :data="superAdminStore.allUsers"
      :striped="true"
      :narrowed="true"
      :hoverable="true"
      :sort-icon="'angle-up'"
      paginated
      :per-page="filter.perPage"
      backend-pagination
      :total="nusers"
      @page-change="pageChange"
      backend-sorting
      @sort="sort"
      backend-filtering
      @filters-change="filtersChange"
    >
      <b-table-column
        sortable
        field="is_active"
        :label="$t('compSuperadminUsersAdmin.active')"
        v-slot="props"
      >
        <b-tag rounded v-if="props.row.isActive" type="is-success">
          {{ $t('compSuperadminUsersAdmin.active') }}
        </b-tag>
        <b-tag rounded v-else type="is-danger">
          {{ $t('compSuperadminUsersAdmin.inactive') }}
        </b-tag>
      </b-table-column>
      <b-table-column
        sortable
        field="last_login"
        :label="$t('compSuperadminUsersAdmin.last-login')"
        custom-key="last_login"
        v-slot="props"
      >
        {{ $d(props.row.lastLogin) }}
      </b-table-column>

      <b-table-column
        sortable
        searchable
        field="email"
        :label="$t('compSuperadminUsersAdmin.email')"
        v-slot="props"
      >
        {{ props.row.email }}
      </b-table-column>

      <b-table-column
        sortable
        field="entity"
        :label="$t('compSuperadminUsersAdmin.entity')"
        v-slot="props"
      >
        {{ props.row.entity }}
      </b-table-column>

      <b-table-column
        numeric
        field="nghgi"
        :label="$t('compSuperadminUsersAdmin.nghgi')"
        v-slot="props"
      >
        {{ props.row.nghgi }}
      </b-table-column>

      <b-table-column
        centered
        sortable
        field="is_superuser"
        :label="$t('compSuperadminUsersAdmin.superuser')"
        v-slot="props"
      >
        <!-- don't allow to remove the last admin -->
        <b-checkbox
          v-model="props.row.isSuperUser"
          @input="(value) => superUserInput(props.row.email, value)"
          :disabled="nsuperusers == 1 && props.row.isSuperUser"
        >
        </b-checkbox>
      </b-table-column>
      <b-table-column
        field="roles"
        :label="$t('compSuperadminUsersAdmin.roles')"
        searchable
      >
        <template #searchable="props">
          <b-taginput
            :allow-duplicate="false"
            ellipsis
            :data="searchabledFilteredRoles"
            autocomplete
            @typing="getSearchableFilteredRoles"
            @input="rolesChange"
            :allow-new="false"
            :open-on-focus="true"
            :icon="'caret-right'"
            :placeholder="$t('add')"
            ref="search-roles"
          >
            <!-- suggested items -->
            <template slot-scope="props">
              <b-tag>
                {{ $t('compSuperadminUsersAdmin.' + props.option.descriptor) }}
              </b-tag>
            </template>

            <!-- selected items -->
            <template #selected="props">
              {{ props.column }}
              <b-tag
                v-for="(role, index) in props.tags"
                :key="index"
                closable
                @close="$refs['search-roles'].removeTag(index, $event)"
              >
                {{ $t('compSuperadminUsersAdmin.' + role.descriptor) }}
              </b-tag>
            </template>
          </b-taginput>
        </template>
        <template v-slot="props">
          <b-taginput
            ellipsis
            :allow-duplicate="false"
            :data="filteredRoles"
            autocomplete
            @typing="getFilteredRoles"
            :allow-new="false"
            :open-on-focus="true"
            :icon="'caret-right'"
            :placeholder="$t('add')"
            @input="(value) => updateRoles(props.row, value)"
            @add="(value) => addRole(props.row, value)"
            @remove="(value) => removeRole(props.row, value)"
            :ref="`rolesinput-${props.row.email}`"
            v-model="props.row.roles"
          >
            <!-- suggested items -->
            <template slot-scope="allRoles">
              <b-tag>
                {{
                  $t('compSuperadminUsersAdmin.' + allRoles.option.descriptor)
                }}
              </b-tag>
            </template>
            <!-- selected items -->
            <template #selected="rolesTags">
              <b-tag
                v-for="(role, index) in rolesTags.tags"
                :key="`userRoles-${index}`"
                closable
                @close="
                  $refs['rolesinput-' + props.row.email].removeTag(
                    `userRoles-${index}`,
                    $event
                  )
                "
              >
                {{ $t('compSuperadminUsersAdmin.' + role.descriptor) }}
              </b-tag>
            </template>
          </b-taginput>
        </template>
      </b-table-column>
      <b-table-column :width="40" v-slot="props">
        <div class="panel-block">
          <b-tooltip
            :label="$t('edit')"
            type="is-black"
            :data-testid="`update-${props.row.email}`"
          >
            <b-button
              size="is-small"
              icon-left="pencil"
              @click="editUser(props.row)"
            />
          </b-tooltip>
          <b-tooltip
            :label="$t('compSuperadminUsersAdmin.send-email')"
            type="is-black"
          >
            <b-button
              :disabled="props.row.isActive"
              size="is-small"
              icon-left="paper-plane"
              @click="sendEmail(props.row)"
            />
          </b-tooltip>
          <b-tooltip :label="$t('delete')" type="is-black">
            <b-button
              size="is-small"
              icon-left="trash"
              @click="deleteUser(props.row)"
            />
          </b-tooltip>
        </div>
      </b-table-column>
      <section slot="empty" class="section">
        <div class="content has-text-grey has-text-centered">
          <p>
            <i class="fa fa-ban" style="font-size: 64px"></i>
          </p>
          <slot name="empty-actions">
            <p>{{ $t('compSuperadminUsersAdmin.no-user') }}</p>
          </slot>
        </div>
      </section>
    </b-table>

    <user-modal
      :displayModal="displayModal"
      :item="cuser"
      :error="error"
      @update="updateUser"
      @close="closeModal"
    />
  </div>
</template>

<script>
import { Background } from '@/mixins/background.js'
import { User, ALL_ROLES } from '@/models/user/user.js'
import { useSuperadminStore } from '@/stores/superadmin.js'

import _ from 'lodash'

export default {
  name: 'UsersAdmin',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'user-modal': () => import('@/components/superadmin/UserModal.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  mixins: [Background],
  data () {
    return {
      // read-only
      allRoles: ALL_ROLES,
      filteredRoles: ALL_ROLES,
      searchabledFilteredRoles: ALL_ROLES,
      displayModal: false,
      cuser: new User(),
      // backend pagination + sorting
      filter: { perPage: 20, page: 1 },
      searchRoles: [],
      // modal to display in the modal
      error: ''
    }
  },
  computed: {
    ninactives: function () {
      return this.superAdminStore.totalInactives
    },
    nusers: function () {
      return this.superAdminStore.totalUsers
    },
    nreviewers: function () {
      return this.superAdminStore.totalReviewers
    },
    nsuperusers: function () {
      return this.superAdminStore.totalAdmin
    }
  },
  methods: {
    rolesChange (roles) {
      this.filter.roles = roles.map((r) => r.descriptor)
      this.filterAll()
    },
    sort (key, order) {
      // reset opposite sort
      this.filter.sort = key
      this.filter.sort_order = order

      this.filterAll()
    },
    filtersChange: _.debounce(function (params) {
      if (params.roles && params.roles.length > 0) {
        // inject descriptor onlu
        params.roles = params.roles.map((r) => r.descriptor)
      }
      Object.assign(this.filter, params)
      this.filterAll()
    }, 400),
    pageChange (page) {
      this.filter['page'] = page
      this.filterAll()
    },
    editUser (user) {
      this.cuser = user.clone()
      this.displayModal = true
    },
    closeModal () {
      this.displayModal = false
      this.error = ''
      this.cuser = new User()
    },
    updateUser (data) {
      this.superAdminStore
        .updateUser(data)
        .then((data) => {
          this.displayModal = false
        })
        .catch((error) => {
          this.error = error.response.data.detail
        })
    },
    deleteUser (data) {
      let self = this
      let msg =
        this.$t('compSuperadminUsersAdmin.message-1-1') +
        '<strong>' +
        data.email +
        '</strong> ?'
      let title = this.$t('compSuperadminUsersAdmin.title-1')
      if (data.isActive) {
        msg =
          this.$t('compSuperadminUsersAdmin.message-2-1') +
          '<strong>' +
          data.email +
          '</strong> ?'
        title = this.$t('compSuperadminUsersAdmin.title-2')
      }
      this.$buefy.dialog.confirm({
        title: title,
        message: msg,
        confirmText: self.$t('validate'),
        cancelText: self.$t('cancel'),
        canCancel: ['escape', 'button', 'outside'],
        onConfirm: function () {
          const loadingComponent = self.$buefy.loading.open({
            container: null
          })
          if (data.isActive) {
            data.setIsActive(false)
            self.superAdminStore
              .updateUser({
                email: data.email,
                item: data
              })
              .then((data) => {
                loadingComponent.close()
              })
          } else {
            self.superAdminStore.deleteUser(data).then((data) => {
              loadingComponent.close()
            })
          }
        }
      })
    },
    superUserInput (email, value) {
      // guard, don't allow to suppress the last admin
      this.superAdminStore.updateUserIsAdmin({
        email: email,
        value: value
      })
    },
    getFilteredRoles (text) {
      this.filteredRoles = this.allRoles.filter((r) => r.descriptor.match(text))
    },
    getSearchableFilteredRoles (text) {
      this.searchabledFilteredRoles = this.allRoles.filter((r) =>
        r.descriptor.match(text)
      )
    },
    translateRole (role) {
      return role.translate(this.$i18n.locale)
    },
    updateRoles (user, roles) {
      this.superAdminStore.updateRoles({
        email: user.email,
        roles: roles.map((r) => r.descriptor)
      })
    },
    addRole (user, role) {
      // updateCounter
      if (role.descriptor === 'reviewer') {
        this.superAdminStore.totalReviewers++
      }
    },
    removeRole (user, role) {
      // update counter
      if (role.descriptor === 'reviewer') {
        this.superAdminStore.totalReviewers--
      }
    },
    sendEmail (user) {
      this.bgUI(
        async () => {
          await this.superAdminStore.sendActivationEmail(user.email)
        },
        this.toast(true, 'email-sent'),
        this.toast(false, 'email-sent')
      )
    },
    filterAll () {
      this.superAdminStore.getUsers(this.filter)
    }
  },
  setup () {
    const superAdminStore = useSuperadminStore()
    return { superAdminStore }
  },
  mounted () {
    this.bgUI(async () => {
      await this.filterAll()
    })
  }
}
</script>
